import React, { useEffect, useRef, useState } from "react";
import FrontendLayout from "../../layouts/FrontendLayout";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL, fetchData } from "../../api/api";
import Spinner from "../../components/common/Spinner";
import Select from "react-select";
import {
  BASEURL,
  CONSTANT_GENDER,
  CONSTANT_REDIRECT_TIME,
} from "../../constant/common";
import { extractSlugDetails } from "../../utils/slug";
import { toast } from "react-toastify";
import PracticeDetailCard from "../../components/frontend/practices/PracticeDetailCard";
import { Helmet } from "react-helmet-async";

const FrontendPracticesDetailPage = () => {
  const location = useLocation();
  const canonicalUrl = `${BASEURL}${location.pathname}`;
  const navigate = useNavigate();

  const { postcode, slug, practice_name_slug } = useParams();

  const { services, suburb } = extractSlugDetails(slug);

  const didFetch = useRef(false);

  const [isLoading, setIsLoading] = useState(false);

  const [practiceDetails, setPracticeDetails] = useState([]);

  const [postcodeData, setPostcodeData] = useState([]);
  const [suburbData, setSuburbData] = useState([]);
  const [servicesData, setServicesData] = useState([]);

  const getPracticeDetails = async () => {
    setIsLoading(true);
    try {
      const data = await fetchData("/detail-frontend-practices", {
        services: services,
        postcode: postcode,
        suburb: suburb,
        slug: practice_name_slug,
      });

      if (data.error === true) {
        navigate("/");
      } else {
        setPracticeDetails(data?.practices);

        setServicesData(data?.services);
        setSuburbData(data?.suburb);
        setPostcodeData(data?.postcode);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!didFetch.current) {
      getPracticeDetails();
      didFetch.current = true;
    }
  }, []);

  useEffect(() => {
    if (practiceDetails?.practice_name) {
      const title = `${practiceDetails?.practice_name} in ${practiceDetails?.suburb?.name} ${practiceDetails?.state?.name}
                      ${practiceDetails?.postcode?.postcode}`;

      const description = `${practiceDetails?.practice_name} in ${practiceDetails?.suburb?.name} ${practiceDetails?.state?.name}
                      ${practiceDetails?.postcode?.postcode}.`;

      document.title = title;

      let descTag = document.querySelector('meta[name="description"]');
      if (!descTag) {
        descTag = document.createElement("meta");
        descTag.setAttribute("name", "description");
        document.head.appendChild(descTag);
      }
      descTag.setAttribute("content", description);
    }
  }, [practiceDetails]);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <FrontendLayout>
        <section className="pt-6 sm:pt-8 md:pt-12 lg:pt-16 xl:pt-[70px] pb-[60px] md:pb-[100px]">
          <div className="container px-3 mx-auto">
            <nav className="flex pb-4" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
                <li className="inline-flex items-center">
                  <Link
                    to={"/"}
                    className="inline-flex items-center font-poppins font-medium text-base text-dark hover:text-blue"
                  >
                    Compare
                  </Link>
                </li>
                <li>
                  <div className="flex items-center">
                    <svg
                      className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 6 10"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="m1 9 4-4-4-4"
                      />
                    </svg>
                    <Link
                      to={`/${postcode}/${slug}`}
                      className="ms-1 font-poppins font-medium text-base text-dark hover:text-blue"
                    >
                      {servicesData?.name} In {suburbData?.name}
                    </Link>
                  </div>
                </li>
                <li aria-current="page">
                  <div className="flex items-center">
                    <svg
                      className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 6 10"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="m1 9 4-4-4-4"
                      />
                    </svg>
                    <span className="ms-1 font-poppins font-medium text-base text-gray-600">
                      {practiceDetails?.practice_name}
                    </span>
                  </div>
                </li>
              </ol>
            </nav>

            <PracticeDetailCard
              item={practiceDetails}
              services={services}
              suburb={suburb}
              postcode={postcode}
              slug={slug}
              practice_name_slug={practice_name_slug}
              getPracticeDetails={getPracticeDetails}
            />
          </div>
        </section>
      </FrontendLayout>
    </>
  );
};

export default FrontendPracticesDetailPage;
